import {
  AreaLine,
  AreaLineRef,
  useIsMobile
} from '@yes.technology/react-toolkit'

import { TopAreaLayoutType, TopAreaPositionType } from 'site/shared/types'

import useStyleMaps from 'site/Renderer/hooks/useStyleMap'

import CollapsibleMenu from './CollapsibleMenu/CollapsibleMenu'
import DesktopLanguageContainer from './LanguageMenu/DesktopLanguageContainer'
import Position from './Position'

import { StyledContainer } from './styles'
import { TopLineProps } from './TopLine.types'
import { useEffect, useRef } from 'react'

enum PositionEnum {
  Position1 = 'position_1',
  Position2 = 'position_2',
  Position3 = 'position_3'
}

type RenderArea = {
  position?: TopAreaPositionType[]
  positionEnum?: PositionEnum
  ref?: React.RefObject<HTMLDivElement>
}

export const renderArea = ({
  ref,
  position,
  positionEnum
}: RenderArea = {}) => {
  const isLeft = positionEnum === PositionEnum.Position1
  const isCenter = positionEnum === PositionEnum.Position2
  const isRight = positionEnum === PositionEnum.Position3

  const id = isRight ? 'navigation-right' : undefined

  const className = `d-flex gap-3 ${isCenter ? 'w-full' : ''}`

  if (ref?.current?.style) {
    ref.current.style.flex = isRight ? 'unset' : isLeft ? '0' : '1'
  }

  return (
    <div id={id} className={className}>
      {position?.map((component, index) => (
        <Position key={index} {...component} />
      ))}
    </div>
  )
}

export const parseLayout = (
  layout?: TopAreaLayoutType | string
): TopAreaLayoutType | undefined => {
  let parsedData

  if (typeof layout === 'string') {
    try {
      parsedData = JSON.parse(layout) as TopAreaLayoutType
    } catch (error) {
      parsedData = {}
    }
  } else {
    parsedData = layout
  }

  return parsedData
}

const TopLine = ({ props }: TopLineProps) => {
  const layout = parseLayout(props.layout)

  const styleProp = useStyleMaps(props?.styleMap)

  const isMobile = useIsMobile()

  const placement = props.placement || 'default'

  const availableLanguages = props.available_languages

  const areaLineRef = useRef<AreaLineRef>(null)

  useEffect(() => {
    if (isMobile) return
    if (!areaLineRef.current) return
    const { leftRef, centerRef, rightRef } = areaLineRef.current
    leftRef.current?.style && (leftRef.current.style.flex = '0')
    centerRef.current?.style && (centerRef.current.style.flex = '1')
    rightRef.current?.style && (rightRef.current.style.flex = 'unset')
  }, [isMobile])

  if (!layout) return null

  if (isMobile) {
    return (
      <CollapsibleMenu
        layout={layout}
        availableLanguages={availableLanguages}
      />
    )
  }

  if (placement !== 'default') {
    return (
      <AreaLine
        ref={areaLineRef}
        className='navbar-expand-lg'
        leftAreaElement={renderArea({
          position: layout.position_1,
          positionEnum: PositionEnum.Position1,
          ref: areaLineRef.current?.leftRef
        })}
        centerAreaElement={renderArea({
          position: layout.position_2,
          positionEnum: PositionEnum.Position2,
          ref: areaLineRef.current?.centerRef
        })}
        rightAreaElement={
          availableLanguages ? (
            <DesktopLanguageContainer availableLanguages={availableLanguages}>
              {renderArea({
                position: layout.position_3,
                positionEnum: PositionEnum.Position3,
                ref: areaLineRef.current?.rightRef
              })}
            </DesktopLanguageContainer>
          ) : (
            renderArea({
              position: layout.position_3,
              positionEnum: PositionEnum.Position3,
              ref: areaLineRef.current?.rightRef
            })
          )
        }
        alignment='left'
        colorScheme='white'
        heightClass='regular'
        placement={placement}
        style={styleProp}
      />
    )
  }

  return (
    <StyledContainer style={styleProp}>
      <AreaLine
        ref={areaLineRef}
        className='navbar-expand-lg'
        leftAreaElement={renderArea({
          position: layout.position_1,
          positionEnum: PositionEnum.Position1,
          ref: areaLineRef.current?.leftRef
        })}
        centerAreaElement={renderArea({
          position: layout.position_2,
          positionEnum: PositionEnum.Position2,
          ref: areaLineRef.current?.centerRef
        })}
        rightAreaElement={renderArea({
          position: layout.position_3,
          positionEnum: PositionEnum.Position3,
          ref: areaLineRef.current?.rightRef
        })}
        alignment='left'
        colorScheme='white'
        heightClass='regular'
        placement={placement}
      />
    </StyledContainer>
  )
}

export default TopLine
